class OblivionStore {
  constructor() {
    this.routes = window.oblivionRoutes || { root: '/', cart: '/cart', search: '/search' };
    this.cartOverlay = document.querySelector('[data-cart-overlay]');
    this.searchOverlay = document.querySelector('[data-search-overlay]');
    this.mobileShopOverlay = document.querySelector('[data-mobile-shop-overlay]');
    this.currency = document.documentElement.lang === 'en' ? 'USD' : 'USD';
    this.bind();
    this.refreshCart();
    this.initReveals();
    this.refreshStockCards();
    this.stockCardTimer = setInterval(() => { if (!document.hidden) this.refreshStockCards(); }, 30000);
    window.addEventListener('focus', () => this.refreshStockCards());
  }

  bind() {
    document.querySelectorAll('[data-cart-open]').forEach(button => button.addEventListener('click', () => this.openCart()));
    document.querySelectorAll('[data-cart-close]').forEach(button => button.addEventListener('click', () => this.closeCart()));
    document.querySelectorAll('[data-search-open]').forEach(button => button.addEventListener('click', () => this.openSearch()));
    document.querySelectorAll('[data-search-close]').forEach(button => button.addEventListener('click', () => this.closeSearch()));
    document.querySelectorAll('[data-shop-open]').forEach(button => button.addEventListener('click', () => this.openMobileShop()));
    document.querySelectorAll('[data-shop-close]').forEach(button => button.addEventListener('click', () => this.closeMobileShop()));
    document.querySelectorAll('[data-quick-add]').forEach(form => form.addEventListener('submit', event => this.addToCart(event)));
    document.querySelectorAll('[data-product-form]').forEach(form => this.initProductForm(form));
    document.querySelectorAll('[data-filter]').forEach(button => button.addEventListener('click', () => this.filterProducts(button)));
    document.querySelector('[data-sort-products]')?.addEventListener('change', event => this.sortProducts(event.target.value));
    document.querySelector('[data-predictive-search]')?.addEventListener('input', event => this.predictiveSearch(event.target.value));
    document.querySelectorAll('[data-search-prompt]').forEach(button => button.addEventListener('click', () => {
      const input = document.querySelector('[data-predictive-search]');
      input.value = button.dataset.searchPrompt;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    }));
    document.addEventListener('keydown', event => {
      if (event.key === 'Escape') { this.closeCart(); this.closeSearch(); this.closeMobileShop(); }
    });
  }

  money(cents) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency, maximumFractionDigits: 2 }).format(cents / 100);
  }

  async addToCart(event) {
    event.preventDefault();
    const form = event.currentTarget;
    const button = form.querySelector('button[type="submit"]');
    const productForm = form.hasAttribute('data-product-form');
    const canPurchase = () => !productForm || ['in-stock', 'preorder'].includes(button?.dataset.purchaseState);
    const variantId = form.dataset.currentVariantId || form.querySelector('[name="id"]')?.value;
    if (!button || button.disabled || !variantId || !canPurchase() || form.dataset.cartSubmitting === 'true') return;
    form.dataset.cartSubmitting = 'true';
    button.disabled = true;
    form.dispatchEvent(new Event('oblivion:cart-state'));
    try {
      if (form.dataset.stockOnly === 'true') {
        const stockUrl = new URL(form.dataset.stockProductUrl, location.origin);
        stockUrl.searchParams.set('view', 'stock-check');
        stockUrl.searchParams.set('variant', variantId);
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) stockUrl.searchParams.set('preview_theme_id', preview);
        const stockResponse = await fetch(stockUrl, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(10000), headers: { Accept: 'text/html' } });
        if (!stockResponse.ok) throw new Error('Could not check current stock.');
        const stock = await stockResponse.json();
        const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
        if (stock.id !== variantId || !stock.available || !Number.isInteger(stock.quantity) || !Number.isInteger(quantity) || quantity < 1 || quantity > stock.quantity) throw new Error('This quantity is no longer in stock.');
      }
      const formData = new FormData(form);
      formData.set('id', variantId);
      const response = await fetch(`${this.routes.root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: formData });
      if (!response.ok) throw new Error('Unable to add this product.');
      button.textContent = button.dataset.purchaseState === 'preorder' ? 'Preorder added ✓' : 'Added to bag ✓';
      await this.refreshCart();
      this.openCart();
    } catch (error) {
      button.textContent = error.message;
    } finally {
      delete form.dataset.cartSubmitting;
      button.disabled = !canPurchase();
      form.dispatchEvent(new Event('oblivion:cart-state'));
      form.dispatchEvent(new Event('oblivion:stock-refresh'));
    }
  }

  initProductForm(form) {
    const configurator = form.querySelector('[data-native-option-values]');
    if (!configurator) {
      if (form.dataset.stockOnly === 'true') {
        const refresh = async () => {
          if (document.hidden || form.dataset.cartSubmitting === 'true') return;
          const button = form.querySelector('[data-purchase-button]');
          if (!button) return;
          try {
            const stock = await this.stockSummary(form.dataset.stockProductUrl);
            button.dataset.purchaseState = stock.stocked ? 'in-stock' : 'sold-out';
            button.disabled = !stock.stocked;
            button.textContent = stock.stocked ? `Add to bag ${form.closest('.main-product').querySelector('[data-product-price]').textContent}` : 'Sold out';
            form.querySelector('[data-dynamic-checkout]')?.setAttribute('hidden', '');
          } catch { button.disabled = true; button.dataset.purchaseState = 'error'; }
        };
        refresh();
        const timer = setInterval(refresh, 30000);
        window.addEventListener('focus', refresh);
        window.addEventListener('pagehide', () => clearInterval(timer));
      }
      return;
    }
    if (configurator.dataset.pickerBound === 'true') return;
    configurator.dataset.pickerBound = 'true';
    const section = form.closest('.main-product');
    const variantInput = form.querySelector('[data-variant-id]');
    const button = form.querySelector('[data-purchase-button]');
    const price = section?.querySelector('[data-product-price]');
    const preorderNotice = form.querySelector('[data-preorder-notice]');
    const preorderProperties = [...form.querySelectorAll('[data-preorder-property]')];
    const dynamicCheckout = form.querySelector('[data-dynamic-checkout]');
    const message = form.querySelector('[data-selection-message]');
    const stockKey = form.querySelector('[data-stock-key]');
    if (!section || !variantInput || !button || !price || !message) return;
    const stockKeyMarkup = stockKey?.innerHTML;
    const stockOnly = form.dataset.stockOnly === 'true';
    const groups = (root = configurator) => [...root.querySelectorAll('[data-option-group]')]
      .sort((a, b) => Number(a.dataset.optionIndex) - Number(b.dataset.optionIndex));
    const selectedButtons = (root = configurator) => groups(root).map(group => group.querySelector('[data-option-button][aria-pressed="true"]'));
    const selectedValues = () => selectedButtons().map(choice => choice?.dataset.optionValue || '');
    const selectedIds = (root = configurator) => selectedButtons(root).map(choice => choice?.dataset.optionValueId || '');
    const purchasable = state => stockOnly ? state === 'in-stock' : ['in-stock', 'preorder'].includes(state);
    let revision = 0;
    let selectionRequest;
    let stockRevision = 0;
    let stockRequest;
    let stockPromise;
    let stockReady = false;
    let stockLoadedAt = 0;
    let stockTuples = [];

    const clearPurchase = (state, text) => {
      variantInput.value = '';
      variantInput.setAttribute('value', '');
      delete form.dataset.currentVariantId;
      button.dataset.purchaseState = state;
      button.disabled = true;
      button.textContent = state === 'loading' ? 'Checking…' : 'Unavailable';
      price.textContent = '';
      if (preorderNotice) preorderNotice.hidden = true;
      preorderProperties.forEach(input => { input.disabled = true; });
      if (dynamicCheckout) dynamicCheckout.hidden = true;
      message.textContent = text;
      message.hidden = false;
    };

    const setSelectionUrl = (ids, id = '') => {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      if (id) {
        url.searchParams.set('variant', id);
        url.searchParams.delete('option_values');
      } else {
        url.searchParams.delete('variant');
        url.searchParams.set('option_values', ids.join(','));
      }
      window.history.replaceState({}, '', url);
    };

    const stockedUnder = (index, value, selected = selectedValues()) => stockReady && stockTuples.some(tuple =>
      tuple[index] === value && tuple.slice(0, index).every((option, previous) => option === selected[previous])
    );

    const setStockCue = (choice, inStock, available, exact = false) => {
      choice.dataset.variantState = inStock ? 'in-stock' : available ? 'available' : 'sold-out';
      if (inStock) {
        const description = exact ? 'This combination is in stock.' : 'In-stock combinations are available under this option.';
        choice.title = description;
        choice.setAttribute('aria-description', description);
      } else {
        choice.removeAttribute('title');
        choice.removeAttribute('aria-description');
      }
    };

    const paintStockCues = () => {
      const optionGroups = groups();
      const selected = selectedValues();
      const submitting = form.dataset.cartSubmitting === 'true';
      optionGroups.forEach((group, index) => group.querySelectorAll('[data-option-button]').forEach(choice => {
        const available = stockOnly ? stockedUnder(index, choice.dataset.optionValue, selected) : choice.dataset.optionAvailable === 'true';
        if (stockOnly) choice.hidden = !stockReady || !available;
        choice.disabled = submitting || !available;
        setStockCue(choice, available && stockedUnder(index, choice.dataset.optionValue, selected), available, index === optionGroups.length - 1);
      }));
      const sizeGroup = optionGroups.find(group => group.dataset.optionIndex === '1');
      const gaugeGroup = configurator.querySelector('[data-doublette-gauge-group]');
      const lengthGroup = configurator.querySelector('[data-doublette-length-group]');
      if (!sizeGroup || !gaugeGroup || !lengthGroup) return;
      const sizes = [...sizeGroup.querySelectorAll('[data-option-button]')];
      const [selectedGauge = '', selectedLength = ''] = (selected[1] || '').split(' · ');
      gaugeGroup.querySelector('[data-doublette-gauge-selection]').textContent = selectedGauge;
      lengthGroup.querySelector('[data-doublette-length-selection]').textContent = selectedLength;
      gaugeGroup.querySelectorAll('[data-doublette-gauge-button]').forEach(choice => {
        const gauge = choice.dataset.doubletteGauge;
        const targetExists = sizes.some(size => size.dataset.optionValue === `${gauge} · ${selectedLength}`);
        const available = stockOnly ? stockReady && stockTuples.some(tuple => tuple[0] === selected[0] && tuple[1].startsWith(`${gauge} · `)) : targetExists && sizes.some(size => size.dataset.optionValue.startsWith(`${gauge} · `) && size.dataset.optionAvailable === 'true');
        if (stockOnly) choice.hidden = !stockReady || !available;
        const inStock = available && stockReady && stockTuples.some(tuple => tuple[0] === selected[0] && tuple[1].startsWith(`${gauge} · `));
        choice.setAttribute('aria-pressed', String(gauge === selectedGauge));
        choice.disabled = submitting || !available;
        setStockCue(choice, inStock, available);
      });
      lengthGroup.querySelectorAll('[data-doublette-length-button]').forEach(choice => {
        const value = `${selectedGauge} · ${choice.dataset.doubletteLength}`;
        const size = sizes.find(candidate => candidate.dataset.optionValue === value) || (stockOnly ? [...configurator.querySelectorAll('[data-option-button][data-option-index="1"]')].find(candidate => candidate.dataset.optionValue.startsWith(`${choice.dataset.doubletteGauge || gauge} · `) && stockedUnder(1, candidate.dataset.optionValue)) : null);
        const available = stockOnly ? stockedUnder(1, value, selected) : size?.dataset.optionAvailable === 'true';
        if (stockOnly) choice.hidden = !stockReady || !available;
        choice.setAttribute('aria-pressed', String(choice.dataset.doubletteLength === selectedLength));
        choice.disabled = submitting || !available;
        setStockCue(choice, available && stockedUnder(1, value, selected), available);
      });
    };

    const normalizeStockSelection = () => {
      if (!stockOnly || !stockReady) return false;
      if (!stockTuples.length) {
        clearPurchase('sold-out', 'This product is currently sold out.');
        return false;
      }
      let candidates = stockTuples;
      const selected = selectedValues();
      let changed = false;
      groups().forEach((group, index) => {
        const value = candidates.some(tuple => tuple[index] === selected[index]) ? selected[index] : candidates[0][index];
        candidates = candidates.filter(tuple => tuple[index] === value);
        changed ||= value !== selected[index];
        group.querySelectorAll('[data-option-button]').forEach(choice => choice.setAttribute('aria-pressed', String(choice.dataset.optionValue === value)));
        group.querySelector('[data-option-selection]').textContent = value;
      });
      return changed;
    };

    const focusToken = choice => choice?.matches('[data-option-button]')
      ? ['optionValueId', choice.dataset.optionValueId]
      : choice?.matches('[data-doublette-gauge-button]')
        ? ['doubletteGauge', choice.dataset.doubletteGauge]
        : choice?.matches('[data-doublette-length-button]')
          ? ['doubletteLength', choice.dataset.doubletteLength]
          : null;

    const requestSelection = async () => {
      if (form.dataset.cartSubmitting === 'true') return;
      const ids = selectedIds();
      if (!ids.length || ids.some(id => !/^\d+$/.test(id))) {
        clearPurchase('error', 'Choose an option to check availability.');
        return;
      }
      const currentRevision = ++revision;
      selectionRequest?.abort();
      const controller = new AbortController();
      selectionRequest = controller;
      const timeout = setTimeout(() => controller.abort(), 10000);
      clearPurchase('loading', 'Checking your selection…');
      form.setAttribute('aria-busy', 'true');
      form.dataset.selectionRevision = String(currentRevision);
      setSelectionUrl(ids);
      paintStockCues();
      refreshStock();
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('variant');
        url.searchParams.set('option_values', ids.join(','));
        url.searchParams.set('section_id', configurator.dataset.sectionId);
        url.hash = '';
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Selection response failed.');
        const html = await response.text();
        if (currentRevision !== revision) return;
        const rendered = new DOMParser().parseFromString(html, 'text/html');
        const nextSection = rendered.querySelector('.main-product');
        const nextForm = nextSection?.querySelector('[data-product-form]');
        const nextConfigurator = nextForm?.querySelector('[data-native-option-values]');
        const nextInput = nextForm?.querySelector('[data-variant-id]');
        const nextButton = nextForm?.querySelector('[data-purchase-button]');
        const nextPrice = nextSection?.querySelector('[data-product-price]');
        const nextMessage = nextForm?.querySelector('[data-selection-message]');
        const nextNotice = nextForm?.querySelector('[data-preorder-notice]');
        const nextProperties = [...(nextForm?.querySelectorAll('[data-preorder-property]') || [])];
        const nextCheckout = nextForm?.querySelector('[data-dynamic-checkout]');
        const state = nextButton?.dataset.purchaseState;
        const nextIds = nextConfigurator ? selectedIds(nextConfigurator) : [];
        if (!nextSection || nextSection.dataset.productId !== configurator.dataset.productId || nextSection.dataset.sectionId !== configurator.dataset.sectionId
          || !nextConfigurator || nextConfigurator.dataset.productId !== configurator.dataset.productId || nextConfigurator.dataset.sectionId !== configurator.dataset.sectionId
          || !nextInput || !nextButton || !nextPrice || !nextMessage || Boolean(nextNotice) !== Boolean(preorderNotice) || Boolean(nextCheckout) !== Boolean(dynamicCheckout)
          || nextProperties.length !== preorderProperties.length || nextProperties.some((input, index) => input.name !== preorderProperties[index].name)
          || nextIds.length !== ids.length || nextIds.some((id, index) => id !== ids[index])
          || !['in-stock', 'preorder', 'sold-out', 'unavailable'].includes(state)
          || (state === 'unavailable' ? nextInput.value !== '' : !/^\d+$/.test(nextInput.value))
          || nextButton.disabled === purchasable(state)
          || (state === 'preorder' && nextProperties.some(input => input.disabled || !input.value))) {
          throw new Error('Incomplete selection response.');
        }
        const focused = configurator.contains(document.activeElement) ? focusToken(document.activeElement) : null;
        configurator.innerHTML = nextConfigurator.innerHTML;
        variantInput.value = nextInput.value;
        variantInput.setAttribute('value', nextInput.value);
        if (nextInput.value) form.dataset.currentVariantId = nextInput.value;
        else delete form.dataset.currentVariantId;
        button.dataset.purchaseState = state;
        button.innerHTML = nextButton.innerHTML;
        button.disabled = !purchasable(state);
        price.textContent = nextPrice.textContent;
        message.textContent = nextMessage.textContent;
        message.hidden = nextMessage.hidden;
        if (preorderNotice) {
          preorderNotice.innerHTML = nextNotice.innerHTML;
          preorderNotice.hidden = state !== 'preorder';
        }
        preorderProperties.forEach((input, index) => {
          input.value = nextProperties[index].value;
          input.disabled = state !== 'preorder';
        });
        if (dynamicCheckout) {
          dynamicCheckout.innerHTML = nextCheckout.innerHTML;
          dynamicCheckout.hidden = state !== 'in-stock';
        }
        paintStockCues();
        setSelectionUrl(ids, nextInput.value);
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (dynamicCheckout && !dynamicCheckout.hidden) window.Shopify?.PaymentButton?.init?.();
        if (focused) [...configurator.querySelectorAll('button')].find(choice => choice.dataset[focused[0]] === focused[1])?.focus({ preventScroll: true });
      } catch {
        if (currentRevision === revision) clearPurchase('error', 'Could not check this selection. Choose an option to retry.');
      } finally {
        clearTimeout(timeout);
        if (currentRevision === revision) {
          form.setAttribute('aria-busy', 'false');
          selectionRequest = null;
        }
      }
    };

    const refreshStock = (force = false) => {
      if (stockPromise && !force) return stockPromise;
      if (!force && stockReady && Date.now() - stockLoadedAt < 30000) return Promise.resolve();
      if (force) stockRequest?.abort();
      const currentRevision = ++stockRevision;
      const controller = new AbortController();
      stockRequest = controller;
      const timeout = setTimeout(() => controller.abort(), 15000);
      stockReady = false;
      configurator.dataset.stockStatus = 'loading';
      paintStockCues();
      const loadPage = async page => {
        const url = new URL(window.location.href);
        ['variant', 'option_values', 'section_id', 'sections'].forEach(key => url.searchParams.delete(key));
        url.searchParams.set('view', 'stock-data');
        url.searchParams.set('page', String(page));
        url.hash = '';
        const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error(`Stock response failed (${response.status}).`);
        const data = await response.json();
        if (data.productId !== configurator.dataset.productId || data.page !== page
          || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100
          || !Number.isInteger(data.pageSize) || data.pageSize < 1 || data.pageSize > 250
          || !Number.isInteger(data.total) || data.total < 1
          || data.pages !== Math.ceil(data.total / data.pageSize)
          || data.offset !== (page - 1) * data.pageSize
          || data.scanned !== Math.min(data.pageSize, data.total - data.offset)
          || !Array.isArray(data.stocked) || data.stocked.length > data.scanned
          || data.stocked.some(tuple => !Array.isArray(tuple) || tuple.length !== groups().length || tuple.some(value => typeof value !== 'string'))) {
          throw new Error('Incomplete stock response.');
        }
        return data;
      };
      stockPromise = (async () => {
        try {
          const first = await loadPage(1);
          const pages = [first];
          for (let page = 2; page <= first.pages; page += 4) {
            const batch = Array.from({ length: Math.min(4, first.pages - page + 1) }, (_, index) => loadPage(page + index));
            pages.push(...await Promise.all(batch));
          }
          if (currentRevision !== stockRevision) return;
          if (pages.some(page => page.total !== first.total || page.pages !== first.pages || page.pageSize !== first.pageSize)
            || pages.reduce((total, page) => total + page.scanned, 0) !== first.total) throw new Error('Stock coverage changed.');
          stockTuples = pages.flatMap(page => page.stocked);
          stockLoadedAt = Date.now();
          stockReady = true;
          configurator.dataset.stockStatus = 'complete';
          configurator.dataset.stockScanned = String(first.total);
          configurator.dataset.stockTotal = String(first.total);
          configurator.dataset.stockPages = String(first.pages);
          delete configurator.dataset.stockError;
          if (stockKey) stockKey.innerHTML = stockKeyMarkup;
          const changedSelection = normalizeStockSelection();
          paintStockCues();
          const exactStocked = stockTuples.some(tuple => tuple.every((value, index) => value === selectedValues()[index]));
          const state = button.dataset.purchaseState;
          if (form.dataset.cartSubmitting !== 'true' && (changedSelection || state === 'in-stock' && !exactStocked || ['preorder', 'sold-out', 'error'].includes(state) && exactStocked)) requestSelection();
        } catch (error) {
          if (currentRevision === stockRevision) {
            controller.abort();
            stockReady = false;
            configurator.dataset.stockStatus = 'unavailable';
            configurator.dataset.stockError = error instanceof SyntaxError ? 'Invalid stock JSON.' : error.message;
            delete configurator.dataset.stockScanned;
            delete configurator.dataset.stockTotal;
            delete configurator.dataset.stockPages;
            if (stockOnly) clearPurchase('error', 'Could not check current stock. Please try again.');
            if (stockKey) stockKey.textContent = 'Check the purchase button for in-stock availability.';
            paintStockCues();
          }
        } finally {
          clearTimeout(timeout);
          if (currentRevision === stockRevision) {
            stockRequest = null;
            stockPromise = null;
          }
        }
      })();
      return stockPromise;
    };

    configurator.addEventListener('click', event => {
      const choice = event.target.closest('[data-option-button], [data-doublette-gauge-button], [data-doublette-length-button]');
      if (!choice || !configurator.contains(choice) || choice.disabled || form.dataset.cartSubmitting === 'true') return;
      let nativeChoice = choice;
      if (!choice.hasAttribute('data-option-button')) {
        const [gauge = '', length = ''] = (selectedValues()[1] || '').split(' · ');
        const value = `${choice.dataset.doubletteGauge || gauge} · ${choice.dataset.doubletteLength || length}`;
        nativeChoice = [...configurator.querySelectorAll('[data-option-button][data-option-index="1"]')].find(candidate => candidate.dataset.optionValue === value);
      }
      if (!nativeChoice) return;
      const group = nativeChoice.closest('[data-option-group]');
      group.querySelectorAll('[data-option-button]').forEach(candidate => candidate.setAttribute('aria-pressed', String(candidate === nativeChoice)));
      group.querySelector('[data-option-selection]').textContent = nativeChoice.dataset.optionValue;
      normalizeStockSelection();
      requestSelection();
    });
    form.addEventListener('oblivion:cart-state', paintStockCues);
    form.addEventListener('oblivion:stock-refresh', () => refreshStock(true));
    window.addEventListener('focus', () => refreshStock(true));
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') refreshStock(true);
    });
    const stockTimer = stockOnly ? setInterval(() => { if (!document.hidden && form.dataset.cartSubmitting !== 'true') refreshStock(true); }, 30000) : null;
    window.addEventListener('pagehide', () => {
      clearInterval(stockTimer);
      selectionRequest?.abort();
      stockRequest?.abort();
    });
    if (variantInput.value) form.dataset.currentVariantId = variantInput.value;
    paintStockCues();
    refreshStock();
  }

  async refreshCart() {
    if (!this.cartOverlay) return;
    const response = await fetch(`${this.routes.root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const cart = await response.json();
    document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = cart.item_count; });
    const items = this.cartOverlay.querySelector('[data-cart-items]');
    items.replaceChildren();
    cart.items.forEach(item => {
      const row = document.createElement('div');
      row.className = 'cart-item';
      const detail = document.createElement('div');
      const title = document.createElement('a');
      title.href = item.url;
      title.textContent = item.product_title;
      detail.append(title);
      if (item.properties?.['Purchase type'] === 'Preorder') {
        const preorder = document.createElement('p');
        preorder.className = 'cart-item-preorder';
        preorder.textContent = `Preorder · ${item.properties['Estimated fulfillment']}`;
        detail.append(preorder);
      }
      const price = document.createElement('span');
      price.textContent = this.money(item.final_line_price);
      row.append(detail, price);
      items.append(row);
    });
    this.cartOverlay.querySelector('[data-cart-empty]').hidden = cart.item_count > 0;
    this.cartOverlay.querySelector('[data-cart-summary]').hidden = cart.item_count === 0;
    this.cartOverlay.querySelector('[data-cart-total]').textContent = this.money(cart.total_price);
  }

  openCart() { if (this.cartOverlay) { this.closeMobileShop(); this.cartOverlay.hidden = false; document.body.classList.add('drawer-open'); } }
  closeCart() { if (this.cartOverlay) { this.cartOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }
  openSearch() { if (this.searchOverlay) { this.closeMobileShop(); this.searchOverlay.hidden = false; document.body.classList.add('drawer-open'); setTimeout(() => this.searchOverlay.querySelector('input')?.focus(), 50); } }
  closeSearch() { if (this.searchOverlay) { this.searchOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }
  openMobileShop() { if (this.mobileShopOverlay) { this.mobileShopOverlay.hidden = false; document.body.classList.add('drawer-open'); this.mobileShopOverlay.querySelector('a')?.focus(); } }
  closeMobileShop() { if (this.mobileShopOverlay) { this.mobileShopOverlay.hidden = true; document.body.classList.remove('drawer-open'); } }

  filterProducts(button) {
    const filter = button.dataset.filter;
    document.querySelectorAll('[data-filter]').forEach(chip => { const active = chip === button; chip.classList.toggle('active', active); chip.setAttribute('aria-pressed', String(active)); });
    let count = 0;
    document.querySelectorAll('[data-product-card]').forEach(card => { const categoryVisible = filter === 'all' || card.dataset.category === filter; card.dataset.categoryHidden = String(!categoryVisible); const visible = categoryVisible && !(card.dataset.stockOnly === 'true' && card.dataset.stocked === 'false'); card.hidden = !visible; if (visible) count += 1; });
    const note = document.querySelector('[data-results-count]');
    if (note) note.textContent = `Showing ${count} ${count === 1 ? 'product' : 'products'}`;
  }
  sortProducts(mode) {
    const grid = document.querySelector('[data-product-grid]');
    if (!grid) return;
    const cards = [...grid.querySelectorAll('[data-product-card]')];
    cards.sort((a, b) => mode === 'high' ? Number(b.dataset.price) - Number(a.dataset.price) : mode === 'low' ? Number(a.dataset.price) - Number(b.dataset.price) : 0).forEach(card => grid.append(card));
  }

  async stockSummary(productUrl) {
    const url = new URL(productUrl, location.origin);
    if (url.origin !== location.origin) throw new Error('Invalid product URL.');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    ['variant', 'option_values', 'section_id', 'sections'].forEach(key => url.searchParams.delete(key));
    url.searchParams.set('view', 'stock-data');
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      let first;
      let scanned = 0;
      for (let page = 1; page <= (first?.pages || 1); page++) {
        url.searchParams.set('page', String(page));
        const response = await fetch(url, { signal: controller.signal, cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Stock unavailable.');
        const data = await response.json();
        first ||= data;
        if (data.productId !== first.productId || data.page !== page || !Number.isInteger(data.pages) || data.pages < 1 || data.pages > 100
          || data.pages !== first.pages || data.total !== first.total || !Number.isInteger(data.pageSize) || data.pageSize < 1 || data.pageSize > 250
          || data.pages !== Math.ceil(data.total / data.pageSize) || data.offset !== (page - 1) * data.pageSize
          || data.scanned !== Math.min(data.pageSize, data.total - data.offset) || !Array.isArray(data.stocked)) throw new Error('Incomplete stock data.');
        scanned += data.scanned;
        if (data.stocked.length && /^\d+$/.test(data.firstStockedId)) return { stocked: true, id: data.firstStockedId };
      }
      if (scanned !== first.total) throw new Error('Incomplete stock scan.');
      return { stocked: false };
    } finally { clearTimeout(timeout); }
  }

  async refreshStockCards() {
    if (this.stockCardsLoading) return;
    this.stockCardsLoading = true;
    try {
      const cards = [...document.querySelectorAll('[data-product-card][data-stock-only="true"]')];
      for (let offset = 0; offset < cards.length; offset += 4) {
        await Promise.all(cards.slice(offset, offset + 4).map(async card => {
          try {
            const stock = await this.stockSummary(card.dataset.stockProductUrl);
            card.dataset.stocked = String(stock.stocked);
            card.hidden = !stock.stocked || card.dataset.categoryHidden === 'true';
            card.dataset.stockStatus = 'complete';
            if (stock.stocked) card.querySelectorAll('a').forEach(link => {
              const url = new URL(link.href);
              url.searchParams.set('variant', stock.id);
              link.href = url;
            });
          } catch { card.dataset.stockStatus = 'unavailable'; }
        }));
      }
    } finally { this.stockCardsLoading = false; }
  }

  async predictiveSearch(query) {
    const searchRevision = this.searchRevision = (this.searchRevision || 0) + 1;
    const output = document.querySelector('[data-predictive-results]');
    if (!output) return;
    if (query.trim().length < 2) { output.replaceChildren(); return; }
    const url = `${this.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return;
    const data = await response.json();
    const resources = data.resources?.results || {};
    const stockedProducts = await Promise.all((resources.products || []).map(async product => {
      if (new URL(product.url, location.origin).pathname.endsWith('/oblivion-x-savvy')) return product;
      try { return (await this.stockSummary(product.url)).stocked ? product : null; } catch { return null; }
    }));
    if (searchRevision !== this.searchRevision) return;
    const results = [...stockedProducts.filter(Boolean), ...(resources.collections || []), ...(resources.pages || []), ...(resources.articles || [])];
    output.replaceChildren();
    results.forEach(result => {
      const link = document.createElement('a');
      link.href = result.url;
      const type = document.createElement('span');
      type.textContent = result.type || 'Archive result';
      const title = document.createElement('strong');
      title.textContent = result.title;
      link.append(type, title);
      output.append(link);
    });
    if (!results.length) output.textContent = 'No results found. Try a material, placement, or service.';
  }

  initReveals() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { targets.forEach(el => el.classList.add('revealed')); return; }
    const observer = new IntersectionObserver(entries => entries.forEach(entry => { if (entry.isIntersecting) entry.target.classList.add('revealed'); }), { threshold: .1 });
    targets.forEach(el => observer.observe(el));
  }
}

document.addEventListener('DOMContentLoaded', () => new OblivionStore());
